import { useCallback, useState } from "react";
import { Users2, RefreshCw, GitMerge, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAppDialog } from "../AppDialog";

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmtSecs = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m ${(n % 60).toString().padStart(2, "0")}s`;
};

/**
 * Admin tool to find + merge visitor rows that share the same linked Google
 * email. Fixes the "coins vanished after re-login" bug where a user got a
 * new visitor_id on a fresh device and started with 0 coins.
 */
const MergeDuplicatesCard = () => {
  const [status, setStatus] = useState("idle"); // idle | scanning | merging | done
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const { confirm } = useAppDialog();

  const scan = useCallback(async ({ keepResult = false } = {}) => {
    setStatus("scanning");
    setError("");
    if (!keepResult) setLastResult(null);
    try {
      const r = await fetch(`${BASE}/api/admin/duplicate-linked`, {
        credentials: "include",
      });
      if (!r.ok) {
        setError(`Scan failed (HTTP ${r.status}).`);
        setStatus("idle");
        return;
      }
      const data = await r.json();
      setGroups(data);
      setStatus("done");
    } catch {
      setError("Network error while scanning.");
      setStatus("idle");
    }
  }, []);

  const merge = useCallback(async (googleEmail) => {
    const ok = await confirm({
      title: googleEmail ? "Merge this group?" : "Merge every duplicate group?",
      message: googleEmail
        ? `All visitor rows linked to ${googleEmail} will be folded into one. This cannot be undone.`
        : "Every set of visitors sharing a Google email will be folded into one row. This cannot be undone.",
      tone: "danger",
      confirmLabel: "Yes, merge",
    });
    if (!ok) return;
    setStatus("merging");
    setError("");
    try {
      const r = await fetch(`${BASE}/api/admin/merge-duplicates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(googleEmail ? { google_email: googleEmail } : {}),
      });
      if (!r.ok) {
        setError(`Merge failed (HTTP ${r.status}).`);
        setStatus("done");
        return;
      }
      const data = await r.json();
      setLastResult(data);
      // Refresh the group list so the merged rows disappear, but KEEP the
      // just-set result so the success banner survives the re-scan.
      await scan({ keepResult: true });
    } catch {
      setError("Network error while merging.");
      setStatus("done");
    }
  }, [scan, confirm]);

  const list = groups?.groups || [];
  const total = groups?.total_duplicates || 0;

  return (
    <section
      className="chunky-card p-4 sm:p-5 mb-6"
      data-testid="admin-merge"
      style={{ background: "#fff" }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-lg sm:text-xl flex items-center gap-2">
          <GitMerge size={22} strokeWidth={3} style={{ color: "#5F27CD" }} />
          Merge duplicate accounts
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => scan()}
            data-testid="admin-merge-scan"
            className="btn-3d btn-sm"
            disabled={status === "scanning" || status === "merging"}
          >
            <RefreshCw
              size={14}
              strokeWidth={3}
              className={status === "scanning" ? "animate-spin" : ""}
            />
            Scan
          </button>
          {list.length > 0 && (
            <button
              type="button"
              onClick={() => merge(null)}
              data-testid="admin-merge-all"
              className="btn-3d btn-sm btn-purple"
              disabled={status === "merging"}
            >
              <GitMerge size={14} strokeWidth={3} />
              Merge all ({list.length})
            </button>
          )}
        </div>
      </div>

      <p className="text-sm mb-3" style={{ color: "#484848" }}>
        <Users2 size={14} strokeWidth={3} style={{ display: "inline", marginRight: 4 }} />
        Some users lose coins when they clear their browser or switch devices
        — a fresh <code>visitor_id</code> is issued and their old row is
        stranded. This tool finds every visitor pair that shares a Google
        email and folds them into the earliest linked account so coins,
        playtime, and progress reconnect.
      </p>

      {error && (
        <div
          data-testid="admin-merge-error"
          className="mb-3 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          style={{ background: "#FFE9E9", color: "#B00020" }}
        >
          <AlertTriangle size={14} strokeWidth={3} />
          {error}
        </div>
      )}

      {lastResult && (
        <div
          data-testid="admin-merge-result"
          className="mb-3 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          style={{ background: "#E9FFE6", color: "#136B22" }}
        >
          <CheckCircle2 size={14} strokeWidth={3} />
          Merged {lastResult.merged_groups} group
          {lastResult.merged_groups === 1 ? "" : "s"} · moved{" "}
          {(lastResult.results || []).reduce(
            (n, r) => n + (r.coins_moved || 0),
            0
          )}{" "}
          coins ·{" "}
          {(lastResult.results || []).reduce(
            (n, r) => n + (r.events_reassigned || 0),
            0
          )}{" "}
          events reassigned.
        </div>
      )}

      {groups === null ? (
        <p className="text-sm" style={{ color: "#8a8a8a" }} data-testid="admin-merge-idle">
          Click <strong>Scan</strong> to look for visitor rows that share a
          Google email.
        </p>
      ) : list.length === 0 ? (
        <p className="text-sm" style={{ color: "#484848" }} data-testid="admin-merge-empty">
          <CheckCircle2
            size={14}
            strokeWidth={3}
            style={{ display: "inline", marginRight: 4, color: "#54D62C" }}
          />
          No duplicate accounts detected — every linked email points to a
          single visitor row.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <p className="text-xs mb-2" style={{ color: "#8a8a8a" }}>
            Found <strong>{list.length}</strong> group
            {list.length === 1 ? "" : "s"} · {total} duplicate row
            {total === 1 ? "" : "s"} across all groups.
          </p>
          <table className="w-full text-sm" data-testid="admin-merge-table">
            <thead style={{ background: "#FFB40033" }}>
              <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
                <th className="text-left py-2 px-2">Google email</th>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-right py-2 px-2">Copies</th>
                <th className="text-right py-2 px-2">Coins (sum)</th>
                <th className="text-right py-2 px-2">Playtime (sum)</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((g) => (
                <tr
                  key={g.google_email}
                  style={{ borderBottom: "1px solid #eee" }}
                  data-testid={`admin-merge-row-${g.google_email}`}
                >
                  <td className="py-2 px-2 font-mono text-xs">{g.google_email}</td>
                  <td className="py-2 px-2">{g.canonical_last_name || "—"}</td>
                  <td className="py-2 px-2 text-right">{g.duplicate_count + 1}</td>
                  <td className="py-2 px-2 text-right">
                    {(g.combined_coins || 0).toLocaleString()}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {fmtSecs(g.combined_playtime_seconds)}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button
                      type="button"
                      onClick={() => merge(g.google_email)}
                      data-testid={`admin-merge-btn-${g.google_email}`}
                      className="btn-3d btn-xs btn-purple"
                      disabled={status === "merging"}
                    >
                      Merge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MergeDuplicatesCard;
